<template>
  <li class="wt-story-version-card">
    <transition name="wt2-fade" @afterLeave="afterLeave">
      <div v-if="!deleted">
        <div class="wt2-poster">
          <a
            v-if="!showActions && !isLockedByAI"
            class="wt2-thumbnail-hover"
            :href="storyLink"
            :class="{ '-in-progress': isLocked }"
            :data-id="version.storyId"
            @click.prevent="open"
          >
            <div
              class="-badge -version-badge"
              :class="{ '-current': isCurrent, '-original': isOriginal }"
            >
              {{ badgeLabel }}
            </div>
            <div class="-info">
              <i v-if="version.type === 'VO'" class="wticon wticon-mic" />
            </div>
            <div
              class="-open-menu js-stop"
              @click.prevent.stop="showActions = true"
            >
              <i class="wticon wticon-dots-three-full js-stop" />
            </div>
          </a>

          <div
            v-if="showActions && !showConfirm"
            class="wt2-story-actions wt2-thumbnail-panel"
          >
            <wt-close @click.native="showActions = false" />

            <span :class="{ disabled: isLocked }" @click="open">
              <i class="wticon wticon-pencil3" />
              {{ 'storyPreview.edit' | culture }}
            </span>

            <span
              v-if="canBranch"
              :class="{ disabled: !isUserCanCreateStories || isLocked }"
              @click="branch"
            >
              <i class="wticon wticon-branch" />
              {{ 'storyVersions.branch' | culture }}
            </span>

            <span
              :class="{ disabled: !isUserCanCreateStories || isLocked }"
              @click="confirmDelete"
            >
              <i class="wticon wticon-trash2" />
              {{ 'storyPreview.remove' | culture }}
            </span>
          </div>

          <wt-thumbnail-confirm
            v-show="showConfirm"
            :message="deleteMessage"
            @no="showConfirm = showActions = false"
            @yes="doDelete"
          />

          <div class="-inner">
            <img
              v-if="isLockedByAI"
              src="/resources/img/lockedAiThumbnail.jpg"
            />
            <img v-else-if="poster" :src="poster" />
            <div v-else class="-no-thumb" />

            <a
              v-if="(isLocked || isLockedByAI) && !showActions"
              class="wt2-story-locked"
              :href="storyLink"
              :data-id="version.storyId"
              @click.prevent
            >
              <i class="wticon wticon-lock2" />
              <div class="wt2-text-n -text">
                <template v-if="isLockedByAI">
                  AI story creation in progress
                </template>
                <template v-else>
                  {{ 'storyPreview.inUse' | culture }}
                  {{ lockerName }}
                </template>
              </div>
              <div class="-info">
                <i v-if="version.type === 'VO'" class="wticon wticon-mic" />
              </div>
            </a>
          </div>
        </div>

        <footer class="wt-preview-item-footer">
          <div class="-top-line">
            <div class="wt2-timestamp wt2-text-s">
              {{ timeAgo }}
            </div>
          </div>
          <div class="-bottom-line">
            <div
              class="wt2-title"
              :title="version.title"
              v-text="version.title || 'Untitled'"
            />
          </div>
          <div
            v-if="aiPrompt"
            class="-ai-prompt wt2-text-s"
            :title="aiPromptTooltip"
            v-text="aiPrompt"
          />
        </footer>
      </div>
    </transition>
  </li>
</template>

<script>
import app from 'app';
import { mapGetters } from 'vuex';
import { isAiLock } from '#/const';

export default {
  name: 'story-version-card',

  props: {
    // A single entry from GET /draftStories/{id}/versions - carries
    // thumbnailUrl and lockedByUserId directly (no per-story preview fetch).
    version: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      showActions: false,
      showConfirm: false,
      deleted: false,
    };
  },

  computed: {
    ...mapGetters(['isUserCanCreateStories']),
    ...mapGetters('storyVersions', ['versionLabel', 'isLockedByOther']),

    isCurrent() {
      return !!this.version.active;
    },

    isOriginal() {
      return !this.version.active && !!this.version.root;
    },

    canBranch() {
      // Branching a root/standalone is a 400 - hide the action there.
      return !this.version.root;
    },

    badgeLabel() {
      const base = this.versionLabel(this.version);
      if (this.isCurrent) {
        return `${base} · ${app.culture.storyVersions.current}`;
      }
      if (this.isOriginal) {
        return `${base} · ${app.culture.storyVersions.original}`;
      }
      return base;
    },

    poster() {
      return this.version.thumbnailUrl
        ? app.prependHttp(this.version.thumbnailUrl)
        : null;
    },

    isLocked() {
      return this.isLockedByOther(this.version);
    },

    isLockedByAI() {
      return isAiLock({
        userId: this.version.lockedByUserId,
        userName: this.version.lockedByUserName,
      });
    },

    lockerName() {
      return (
        this.version.lockedByUserName || app.culture.storyVersions.unknownUser
      );
    },

    storyLink() {
      return `/stories/${this.version.storyId}`;
    },

    timeAgo() {
      const ts = app.moment(this.version.lastModifiedDate).valueOf();
      return app.TIME.getTimeAgo(this.$store.state.currentTime - ts);
    },

    aiPrompt() {
      return this.version.latestAiPrompt || null;
    },

    aiPromptTooltip() {
      return `${app.culture.storyVersions.aiPromptTitle}: ${this.aiPrompt}`;
    },

    deleteMessage() {
      return this.version.root
        ? app.culture.storyVersions.confirmDeleteRoot
        : app.culture.storyVersions.confirmDeleteVersion;
    },
  },

  methods: {
    open() {
      if (this.isLocked) {
        return;
      }
      this.$emit('open', this.version.storyId);
    },

    branch() {
      if (!this.isUserCanCreateStories || this.isLocked) {
        return;
      }
      this.showActions = false;
      this.$emit('branch', this.version.storyId);
    },

    confirmDelete() {
      if (!this.isUserCanCreateStories || this.isLocked) {
        return;
      }
      this.showConfirm = true;
    },

    doDelete() {
      this.deleted = true;
    },

    afterLeave() {
      if (!this.deleted) {
        return;
      }
      this.$emit('delete', this.version.storyId);
    },
  },
};
</script>
